import React, { useState, useRef, useEffect, useContext, useMemo, useCallback } from 'react';
import { UserContext } from '../../context/UserContext';
import {
  Send,
  MessageSquare,
  Plus,
  Sparkles,
  Download,
  Image as ImageIcon,
  Pencil,
  Trash2,
  PanelLeftClose,
  PanelLeft,
  Search,
  ChevronDown,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DEFAULT_VENUE_TYPE, VENUE_TYPES } from '../../lib/consts/venueTypes';
import { getAssistantChartRenderState, mergeAssistantStreamWithFinal, splitMessageForChartExport } from './aiStreamChart';
import AssistantSuggestPill from './AssistantSuggestPill';
import {
  pickRandomAssistantSuggestion,
  groupConversationsByPeriod,
  formatRelativeTime,
  countChatMessages,
  conversationRecencyTimestamp,
  useMediaMinWidth,
  ASSISTANT_DESKTOP_MIN_PX,
  mapStoredChatMessage,
  messageToApiHistoryEntry,
} from './assistantUiHelpers';
import { formatAssistantMessageHtml } from './assistantMarkdown';
import './AsistenteIAPage.css';

function getInitialMessage(venueType = DEFAULT_VENUE_TYPE) {
  if (venueType === VENUE_TYPES.CBD_SHOP) {
    return { role: 'assistant', content: '¡Hola! Soy ClubsAgent AI. ¿En qué puedo ayudarte hoy con tu tienda CBD?' };
  }
  if (venueType === VENUE_TYPES.SOCIAL_CLUB) {
    return { role: 'assistant', content: '¡Hola! Soy ClubsAgent AI. ¿En qué puedo ayudarte hoy con tu club social?' };
  }
  return { role: 'assistant', content: '¡Hola! Soy ClubsAgent AI. ¿En qué puedo ayudarte hoy con tu club?' };
}

function getVenueChangedMessage(venueType = DEFAULT_VENUE_TYPE) {
  if (venueType === VENUE_TYPES.CBD_SHOP) {
    return { role: 'assistant', content: '¡Hola! Has cambiado de tienda. ¿En qué puedo ayudarte con esta tienda CBD?' };
  }
  if (venueType === VENUE_TYPES.SOCIAL_CLUB) {
    return { role: 'assistant', content: '¡Hola! Has cambiado de club. ¿En qué puedo ayudarte con este club social?' };
  }
  return { role: 'assistant', content: '¡Hola! Has cambiado de club. Soy ClubsAgent AI. ¿En qué puedo ayudarte con este club?' };
}

const CACHE_LIST_KEY = (venueId) => `leafgate_ai_conversations_${venueId}`;
const CACHE_MSG_KEY = (convId) => `leafgate_ai_conv_msgs_${convId}`;

function getCachedConversations(venueId) {
  try {
    const raw = localStorage.getItem(CACHE_LIST_KEY(venueId));
    if (!raw) return null;
    const data = JSON.parse(raw);
    return Array.isArray(data) ? data : null;
  } catch {
    return null;
  }
}

function setCachedConversations(venueId, list) {
  try {
    localStorage.setItem(CACHE_LIST_KEY(venueId), JSON.stringify(list));
  } catch {
    // ignore quota or parse errors
  }
}

function getCachedMessages(convId) {
  try {
    const raw = localStorage.getItem(CACHE_MSG_KEY(convId));
    if (!raw) return null;
    const data = JSON.parse(raw);
    return Array.isArray(data) ? data : null;
  } catch {
    return null;
  }
}

function setCachedMessages(convId, messages) {
  try {
    localStorage.setItem(CACHE_MSG_KEY(convId), JSON.stringify(messages));
  } catch {
    // ignore
  }
}

/** Deduplica por id manteniendo el orden (primera aparición). */
function dedupeConversationsById(list) {
  if (!Array.isArray(list) || list.length === 0) return list;
  const seen = new Set();
  return list.filter((c) => {
    const id = c?.id;
    if (id == null || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

function sortConversationsByRecency(list) {
  if (!Array.isArray(list) || list.length === 0) return Array.isArray(list) ? [...list] : [];
  return [...list].sort((a, b) => {
    const tb = conversationRecencyTimestamp(b);
    const ta = conversationRecencyTimestamp(a);
    if (tb !== ta) return tb - ta;
    return String(b.id ?? '').localeCompare(String(a.id ?? ''));
  });
}

function normalizeConversationsList(list, { limit } = {}) {
  const sorted = sortConversationsByRecency(dedupeConversationsById(list));
  if (typeof limit === 'number' && limit > 0 && sorted.length > limit) {
    return sorted.slice(0, limit);
  }
  return sorted;
}

/**
 * Full-page Asistente IA – ChatGPT-style layout.
 * Conversaciones en backend + caché en localStorage (listado por venue, mensajes de la conversación activa).
 */
export default function AsistenteIAPage() {
  const [token, , , venueID, , , , accessPrivileges, allVenues, , , venueType] = useContext(UserContext);
  const [messages, setMessages] = useState([getInitialMessage(venueType)]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState('current');
  const [editingConvId, setEditingConvId] = useState(null);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [convPanelOpen, setConvPanelOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(`(min-width: ${ASSISTANT_DESKTOP_MIN_PX}px)`).matches;
  });
  const isDesktop = useMediaMinWidth(ASSISTANT_DESKTOP_MIN_PX);
  const showConvPanel = convPanelOpen;

  useEffect(() => {
    setConvPanelOpen(isDesktop);
  }, [isDesktop]);
  const [randomSuggestion, setRandomSuggestion] = useState(() =>
    pickRandomAssistantSuggestion(null, DEFAULT_VENUE_TYPE)
  );
  const [sidebarSearch, setSidebarSearch] = useState('');
  const [showScrollFab, setShowScrollFab] = useState(false);
  const messagesEndRef = useRef(null);
  const chatMainRef = useRef(null);
  const inputRef = useRef(null);
  const prevVenueIDRef = useRef(undefined);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isDesktop) return undefined;
    const html = document.documentElement;
    html.classList.add('wa-asistente-lock-scroll');
    return () => {
      html.classList.remove('wa-asistente-lock-scroll');
    };
  }, [isDesktop]);

  const closeConvPanel = useCallback(() => {
    setConvPanelOpen(false);
  }, []);

  const openConvPanel = useCallback(() => {
    setConvPanelOpen(true);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const apiHeaders = React.useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }), [token]);

  const fetchConversations = React.useCallback(async () => {
    if (!token || !venueID) return;
    setConversationsLoading(true);
    try {
      const res = await fetch(`/ai/conversations?venue_id=${encodeURIComponent(venueID)}`, {
        headers: apiHeaders(),
      });
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) return;
        setConversations([]);
        return;
      }
      const data = await res.json();
      const list = normalizeConversationsList(Array.isArray(data) ? data : []);
      setConversations(list);
      setCachedConversations(venueID, list);
    } catch {
      setConversations([]);
    } finally {
      setConversationsLoading(false);
    }
  }, [token, venueID, apiHeaders]);

  useEffect(() => {
    const prev = prevVenueIDRef.current;
    const changed = prev !== undefined && prev !== '' && prev !== venueID;
    if (changed) {
      setMessages([getVenueChangedMessage(venueType)]);
      setActiveConversationId('current');
      setRandomSuggestion(pickRandomAssistantSuggestion(null, venueType));
      setSidebarSearch('');
    }
    prevVenueIDRef.current = venueID;
    if (venueID && token) {
      const cached = getCachedConversations(venueID);
      if (cached !== null) setConversations(normalizeConversationsList(cached));
      fetchConversations();
    }
  }, [venueID, token, venueType, fetchConversations]);

  const handleNewChat = async () => {
    // Solo guardar en backend si estamos en conversación nueva (sin guardar) y tiene contenido.
    // Si estamos viendo una conversación ya guardada, solo cambiar a vista "nueva" sin crear otra.
    const isUnsavedCurrent = activeConversationId === 'current';
    const hasContent = messages.length > 1 || (messages.length === 1 && messages[0].role === 'user');
    if (isUnsavedCurrent && hasContent && token && venueID) {
      const firstUser = messages.find(m => m.role === 'user');
      const title = firstUser
        ? (firstUser.content.slice(0, 40) + (firstUser.content.length > 40 ? '…' : ''))
        : `Conversación ${new Date().toLocaleDateString()}`;
      try {
        const res = await fetch('/ai/conversations', {
          method: 'POST',
          headers: apiHeaders(),
          body: JSON.stringify({ venue_id: venueID, title }),
        });
        if (res.ok) {
          const created = await res.json();
          setConversations(prev => {
            if (prev.some((c) => c.id === created.id)) return normalizeConversationsList(prev);
            const next = normalizeConversationsList([...prev, created]);
            setCachedConversations(venueID, next);
            return next;
          });
        }
      } catch {
        // fallback: solo limpiar local
      }
    }
    setMessages([getInitialMessage(venueType)]);
    setActiveConversationId('current');
    setEditingConvId(null);
    setRandomSuggestion(pickRandomAssistantSuggestion(null, venueType));
  };

  const handleSelectConversation = async (conv) => {
    if (!isDesktop) closeConvPanel();
    if (conv.id === 'current') {
      setMessages([getInitialMessage(venueType)]);
      setActiveConversationId('current');
      setEditingConvId(null);
      return;
    }
    setActiveConversationId(conv.id);
    setEditingConvId(null);
    const cachedMsgs = getCachedMessages(conv.id);
    if (cachedMsgs && cachedMsgs.length > 0) setMessages(cachedMsgs);
    if (!token) return;
    try {
      const res = await fetch(`/ai/conversations/${conv.id}`, { headers: apiHeaders() });
      if (!res.ok) {
        setMessages([getInitialMessage(venueType)]);
        return;
      }
      const data = await res.json();
      const msgs = (data.messages || []).map(mapStoredChatMessage);
      const toSet = msgs.length ? msgs : [getInitialMessage(venueType)];
      setMessages(toSet);
      setCachedMessages(conv.id, toSet);
    } catch {
      setMessages([getInitialMessage(venueType)]);
    }
  };

  const handleRenameConversation = async (convId, newTitle) => {
    const trimmed = (newTitle || '').trim();
    const title = trimmed || 'Conversación';
    const nowIso = new Date().toISOString();
    setConversations(prev => {
      const next = normalizeConversationsList(
        prev.map((c) => (c.id === convId ? { ...c, title, updated_at: nowIso } : c)),
      );
      setCachedConversations(venueID, next);
      return next;
    });
    setEditingConvId(null);
    if (!token) return;
    try {
      const res = await fetch(`/ai/conversations/${convId}`, {
        method: 'PATCH',
        headers: apiHeaders(),
        body: JSON.stringify({ title }),
      });
      if (!res.ok) fetchConversations();
    } catch {
      fetchConversations();
    }
  };

  const handleDeleteConversation = async (e, convId) => {
    e.stopPropagation();
    if (activeConversationId === convId) {
      setMessages([getInitialMessage(venueType)]);
      setActiveConversationId('current');
    }
    setConversations(prev => {
      const next = prev.filter(c => c.id !== convId);
      setCachedConversations(venueID, next);
      return next;
    });
    try {
      localStorage.removeItem(CACHE_MSG_KEY(convId));
    } catch {
      // ignore
    }
    setEditingConvId(editingConvId === convId ? null : editingConvId);
    if (!token) return;
    try {
      await fetch(`/ai/conversations/${convId}`, { method: 'DELETE', headers: apiHeaders() });
    } catch {
      fetchConversations();
    }
  };

  const handleSubmit = async (e, options = {}) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    const userMessage = (typeof options.message === 'string' ? options.message : input).trim();
    if (!userMessage) return;
    if (!token) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'No estás autenticado. Por favor, inicia sesión.' }]);
      return;
    }
    if (!venueID) {
      const hasVenues = Array.isArray(allVenues) && allVenues.length > 0;
      const hint = accessPrivileges === 'superadmin' && hasVenues
        ? 'Selecciona una tienda en el selector superior.'
        : 'Selecciona una tienda primero.';
      setMessages(prev => [...prev, { role: 'assistant', content: `No hay una tienda seleccionada. ${hint}` }]);
      return;
    }
    setInput('');
    const assistantMessageIndex = messages.length + 1;
    setMessages(prev => [...prev, { role: 'user', content: userMessage }, { role: 'assistant', content: '' }]);
    setIsLoading(true);

    let conversationIdToUse = activeConversationId === 'current' ? null : activeConversationId;
    if (activeConversationId === 'current') {
      try {
        const title = userMessage.slice(0, 40) + (userMessage.length > 40 ? '…' : '');
        const createRes = await fetch('/ai/conversations', {
          method: 'POST',
          headers: apiHeaders(),
          body: JSON.stringify({ venue_id: venueID, title }),
        });
        if (createRes.ok) {
          const created = await createRes.json();
          conversationIdToUse = created.id;
          setActiveConversationId(created.id);
          setConversations(prev => {
            if (prev.some((c) => c.id === created.id)) return normalizeConversationsList(prev);
            const next = normalizeConversationsList([...prev, created]);
            setCachedConversations(venueID, next);
            return next;
          });
        }
      } catch {}
    }

    const historyForApi = messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map(messageToApiHistoryEntry);
    const body = JSON.stringify({
      message: userMessage,
      history: historyForApi,
      venue_id: venueID,
      conversation_id: conversationIdToUse || undefined,
    });
    const headers = apiHeaders();

    const getStreamUrl = () => {
      // En dev, el proxy del FE (Express) puede comprimir/bufferizar SSE y “parece” que llega de golpe.
      // Ir directo al backend elimina esa variable para streaming.
      const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
        window.location.port === '3000';
      return isLocalhost ? 'http://127.0.0.1:8000/ai/chat/stream' : '/ai/chat/stream';
    };

    const applyAssistantText = (text, { append, reasoningContent } = { append: false }) => {
      setMessages(prev => {
        const next = [...prev];
        if (!next[assistantMessageIndex] || next[assistantMessageIndex].role !== 'assistant') return prev;
        const prevText = next[assistantMessageIndex].content || '';
        const updated = {
          ...next[assistantMessageIndex],
          content: append ? (prevText + (text || '')) : (text || ''),
        };
        if (reasoningContent) updated.reasoning_content = reasoningContent;
        next[assistantMessageIndex] = updated;
        if (conversationIdToUse) setCachedMessages(conversationIdToUse, next);
        return next;
      });
    };

    const startStreaming = async () => {
      const response = await fetch(getStreamUrl(), {
        method: 'POST',
        headers: { ...headers, Accept: 'text/event-stream' },
        body,
      });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) throw new Error('No autorizado. Inicia sesión de nuevo.');
        if (response.status === 404) throw new Error('El servicio de IA no está disponible.');
        throw new Error(`Error del servidor: ${response.status}`);
      }
      if (!response.body) throw new Error('Stream no disponible.');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let doneSeen = false;
      let assistantSoFar = '';
      let flushTimerId = null;
      let firstFlushDone = false;
      const flushAssistant = () => {
        applyAssistantText(assistantSoFar, { append: false });
      };
      const scheduleFlush = () => {
        if (flushTimerId !== null) return;
        flushTimerId = window.setTimeout(() => {
          flushTimerId = null;
          flushAssistant();
        }, 16);
      };
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (!value) continue;
          const text = decoder.decode(value, { stream: true }).replace(/\r/g, '');
          buffer += text;
          const parts = buffer.split('\n\n');
          buffer = parts.pop() || '';
          for (const part of parts) {
            const lines = part.split('\n');
            const dataLines = [];
            for (const line of lines) {
              if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
            }
            if (dataLines.length === 0) continue;
            const dataStr = dataLines.join('\n');
            let payload;
            try { payload = JSON.parse(dataStr); } catch { continue; }
            if (payload && typeof payload.chunk === 'string' && payload.chunk.length > 0) {
              assistantSoFar += payload.chunk;
              if (!firstFlushDone) {
                firstFlushDone = true;
                // Primer chunk: pintar ya mismo para mejorar percepción.
                flushAssistant();
              } else {
                scheduleFlush();
              }
            }
            if (payload && payload.done) {
              doneSeen = true;
              if (flushTimerId !== null) {
                window.clearTimeout(flushTimerId);
                flushTimerId = null;
              }
              if (payload.error || payload.detail) {
                const errText =
                  typeof payload.detail === 'string'
                    ? payload.detail
                    : typeof payload.error === 'string'
                      ? payload.error
                      : 'Error en el servicio de IA';
                assistantSoFar = `Lo siento, hubo un problema: ${errText}`;
              } else if (typeof payload.response === 'string') {
                assistantSoFar = mergeAssistantStreamWithFinal(assistantSoFar, payload.response);
              }
              const rc =
                typeof payload.reasoning_content === 'string' && payload.reasoning_content.trim()
                  ? payload.reasoning_content.trim()
                  : undefined;
              applyAssistantText(assistantSoFar, { append: false, reasoningContent: rc });
              break;
            }
          }
          if (doneSeen) break;
        }
      } finally {
        if (flushTimerId !== null) {
          window.clearTimeout(flushTimerId);
          flushTimerId = null;
        }
        // Evitar “golpe final” si todavía no se pintó nada.
        if (!firstFlushDone) flushAssistant();
        try { reader.releaseLock(); } catch {}
      }
    };

    const startNonStreaming = async () => {
      const response = await fetch('/ai/chat', { method: 'POST', headers, body });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) throw new Error('No autorizado. Inicia sesión de nuevo.');
        if (response.status === 404) throw new Error('El servicio de IA no está disponible.');
        throw new Error(`Error del servidor: ${response.status}`);
      }
      const data = await response.json();
      const rc =
        typeof data.reasoning_content === 'string' && data.reasoning_content.trim()
          ? data.reasoning_content.trim()
          : undefined;
      applyAssistantText(data.response ?? '', { append: false, reasoningContent: rc });
    };

    try {
      await startStreaming();
    } catch (streamError) {
      try {
        await startNonStreaming();
      } catch (error) {
        const errorMessage = error?.message || streamError?.message || 'Error de conexión';
        const friendly = errorMessage === 'Failed to fetch'
          ? 'No se pudo conectar con el servidor. Verifica que el backend esté corriendo.'
          : errorMessage;
        applyAssistantText(friendly, { append: false });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const convertSvgToBase64 = (svg) => {
    return new Promise((resolve) => {
      try {
        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        const width = svg.clientWidth || 600;
        const height = svg.clientHeight || 300;
        const scale = 2;
        canvas.width = width * scale;
        canvas.height = height * scale;
        ctx.scale(scale, scale);
        img.onload = () => {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => resolve(null);
        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));
      } catch (e) {
        console.error('Error converting SVG', e);
        resolve(null);
      }
    });
  };

  const handleDownloadChat = async () => {
    if (messages.length <= 1) return;
    let htmlContent = `
      <!DOCTYPE html><html><head><meta charset="UTF-8"><title>Historial - ClubsAgent AI</title>
      <style>
        body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;max-width:800px;margin:40px auto;padding:20px;background:#f9fafb;color:#1f2937;}
        .header{text-align:center;margin-bottom:40px;}.header h2{color:#059669;margin:0;font-size:24px;}
        .message{margin-bottom:24px;padding:16px;border-radius:16px;max-width:85%;line-height:1.5;box-shadow:0 1px 2px rgba(0,0,0,0.05);}
        .user{background:#059669;color:white;margin-left:auto;border-bottom-right-radius:4px;}
        .assistant{background:white;border:1px solid #e5e7eb;color:#374151;margin-right:auto;border-bottom-left-radius:4px;}
        .role{font-size:11px;font-weight:700;margin-bottom:6px;text-transform:uppercase;opacity:0.8;}
        .content{font-size:14px;}.content strong{font-weight:700;color:#065f46;}
        .wa-asistente-md-table-wrap{margin:12px 0;overflow-x:auto;border-radius:10px;border:1px solid #e5e7eb;}
        .wa-asistente-md-table{width:100%;border-collapse:collapse;font-size:13px;}
        .wa-asistente-md-table th,.wa-asistente-md-table td{padding:8px 12px;text-align:left;border-bottom:1px solid #e5e7eb;}
        .wa-asistente-md-table th{background:#f3f4f6;font-weight:600;color:#374151;}
        .wa-asistente-md-table tr:last-child td{border-bottom:none;}
        .wa-asistente-md-block-gap{height:0.75rem;}
        .chart-container{background:white;padding:15px;border-radius:12px;margin-top:15px;border:1px solid #e5e7eb;text-align:center;}
        .chart-title{font-size:12px;color:#6b7280;margin-top:10px;}.chart-img{max-width:100%;height:auto;border-radius:8px;}
      </style></head><body>
      <div class="header"><h2>ClubsAgent AI</h2><p>Historial • ${new Date().toLocaleString()}</p></div>`;

    const messagePromises = messages.map(async (msg, idx) => {
      const isUser = msg.role === 'user';
      const roleName = isUser ? 'TÚ' : 'LEAFGATE AI';
      const msgClass = isUser ? 'user' : 'assistant';
      let finalInnerHtml = '';
      const { hasChart, textPart: rawTextPart, chartData: exportChart } = splitMessageForChartExport(msg.content);
      if (hasChart) {
        let textPart = rawTextPart.replace(/\n*\*{0,2}(Respuesta Final|Observación|Pensamiento)\s*:?\*{0,2}\s*\n*/gi, '\n').replace(/\n{3,}/g, '\n\n').trim();
        const chartTitle = exportChart && exportChart.title ? exportChart.title : '';
        finalInnerHtml = formatAssistantMessageHtml(textPart);
        const chartId = `asistente-chart-${idx}`;
        const container = document.getElementById(chartId);
        if (container) {
          const svg = container.querySelector('.recharts-surface');
          if (svg) {
            const base64 = await convertSvgToBase64(svg);
            if (base64) finalInnerHtml += `<div class="chart-container"><img src="${base64}" class="chart-img" alt="Gráfico"/>${chartTitle ? `<div class="chart-title">${chartTitle}</div>` : ''}</div>`;
            else finalInnerHtml += '<div style="color:#999;font-size:12px;">[Error al generar gráfico]</div>';
          } else finalInnerHtml += '<div style="color:#999;font-size:12px;">[Gráfico no disponible]</div>';
        }
      } else {
        const text = msg.content.replace(/\n*\*{0,2}(Respuesta Final|Observación|Pensamiento)\s*:?\*{0,2}\s*\n*/gi, '\n').replace(/\n{3,}/g, '\n\n').trim();
        finalInnerHtml = formatAssistantMessageHtml(text);
      }
      return `<div class="message ${msgClass}"><div class="role">${roleName}</div><div class="content">${finalInnerHtml}</div></div>`;
    });
    const parts = await Promise.all(messagePromises);
    htmlContent += parts.join('') + '</body></html>';
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `chat-clubsagent-${new Date().toISOString().slice(0, 10)}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadChart = (chartId) => {
    const container = document.getElementById(chartId);
    if (!container) return;
    const svg = container.querySelector('.recharts-surface');
    if (!svg) return;
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    const width = svg.clientWidth || 600;
    const height = svg.clientHeight || 300;
    canvas.width = width;
    canvas.height = height;
    img.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      try {
        const pngUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = pngUrl;
        link.download = `grafico-${chartId}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (e) {
        console.error('Error generating image', e);
      }
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));
  };

  const formatMessageHtml = (text, { isUser = false } = {}) =>
    formatAssistantMessageHtml(text, {
      boldClass: isUser ? 'font-semibold' : 'font-semibold text-emerald-800',
    });

  const renderMessageContent = (content, idx) => {
    const chartId = `asistente-chart-${idx}`;
    const state = getAssistantChartRenderState(content);

    if (state.phase === 'plain') {
      return (
        <div
          className="break-words"
          dangerouslySetInnerHTML={{ __html: formatMessageHtml(content) }}
        />
      );
    }

    if (state.phase === 'streaming') {
      return (
        <div className="flex flex-col gap-3">
          {state.prefix ? (
            <div
              className="break-words"
              dangerouslySetInnerHTML={{ __html: formatMessageHtml(state.prefix) }}
            />
          ) : null}
          <div className="text-xs text-slate-500 italic py-2 px-3 rounded-lg border border-dashed border-slate-200 bg-slate-50/90">
            Generando gráfico…
          </div>
        </div>
      );
    }

    const { prefix, chartData } = state;
    const chartOk = chartData && Array.isArray(chartData.data);

    return (
      <div className="flex flex-col gap-3">
        {prefix ? (
          <div
            className="break-words"
            dangerouslySetInnerHTML={{ __html: formatMessageHtml(prefix) }}
          />
        ) : null}
        {chartOk ? (
          <div id={chartId} className="relative w-full h-40 mt-2 bg-white/50 rounded-lg p-2 border border-gray-200 group/chart">
            <button
              type="button"
              onClick={() => handleDownloadChart(chartId)}
              className="absolute top-2 right-2 z-10 p-1.5 bg-white text-emerald-600 rounded-md shadow-sm border border-emerald-100 hover:bg-emerald-50 transition-colors"
              title="Descargar gráfico como imagen"
            >
              <ImageIcon size={14} />
            </button>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData.data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis
                  dataKey="label"
                  fontSize={9}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: '#6B7280' }}
                />
                <YAxis fontSize={9} tickLine={false} axisLine={false} tick={{ fill: '#6B7280' }} />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', fontSize: '12px' }}
                  cursor={{ fill: '#f3f4f6' }}
                />
                <Bar dataKey="value" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            {chartData.title ? (
              <div className="text-[10px] text-center text-gray-400 mt-1 font-medium">{chartData.title}</div>
            ) : null}
          </div>
        ) : (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            El bloque del gráfico llegó cerrado pero el JSON no es válido. Prueba a pedir el gráfico de nuevo.
          </p>
        )}
      </div>
    );
  };

  const filteredConversations = useMemo(() => {
    const q = sidebarSearch.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) => (c.title || '').toLowerCase().includes(q));
  }, [conversations, sidebarSearch]);

  const conversationGroups = useMemo(
    () => groupConversationsByPeriod(filteredConversations),
    [filteredConversations],
  );

  const activeChatTitle = useMemo(() => {
    if (activeConversationId === 'current') {
      const firstUser = messages.find((m) => m.role === 'user');
      if (firstUser?.content) {
        const t = firstUser.content.trim();
        return t.length > 52 ? `${t.slice(0, 52)}…` : t;
      }
      return 'Nueva conversación';
    }
    const active = conversations.find((c) => c.id === activeConversationId);
    return active?.title || 'Conversación';
  }, [activeConversationId, conversations, messages]);

  const chatMetaLine = useMemo(() => {
    const n = countChatMessages(messages);
    let timeLabel = 'Ahora';
    if (activeConversationId !== 'current') {
      const active = conversations.find((c) => c.id === activeConversationId);
      const ts = conversationRecencyTimestamp(active);
      if (ts) timeLabel = formatRelativeTime(ts);
    }
    return `${timeLabel} · ${n} mensaje${n === 1 ? '' : 's'}`;
  }, [activeConversationId, conversations, messages]);

  const handleSuggestPillClick = useCallback(() => {
    const next = pickRandomAssistantSuggestion(randomSuggestion?.id, venueType);
    if (!next?.prompt) return;
    setRandomSuggestion(next);
    setInput(String(next.prompt).trim());
    inputRef.current?.focus();
  }, [randomSuggestion, venueType]);

  const handleChatScroll = useCallback(() => {
    const el = chatMainRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollFab(distance > 120);
  }, []);

  const handleScrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const renderConversationItem = (c) => (
    <div key={c.id} className="group/conversation flex items-center gap-0.5 px-1">
      <button
        type="button"
        onClick={() => handleSelectConversation(c)}
        className={`wa-asistente-conv-item flex-1 min-w-0 ${activeConversationId === c.id ? 'is-active' : ''}`}
      >
        <MessageSquare size={15} className="shrink-0 opacity-70" aria-hidden />
        {editingConvId === c.id ? (
          <input
            type="text"
            value={c.title}
            onChange={(e) =>
              setConversations((prev) =>
                prev.map((x) => (x.id === c.id ? { ...x, title: e.target.value } : x)),
              )
            }
            onBlur={() => handleRenameConversation(c.id, c.title)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRenameConversation(c.id, c.title);
              if (e.key === 'Escape') setEditingConvId(null);
            }}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 min-w-0 rounded border border-gray-300 bg-white px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
            autoFocus
          />
        ) : (
          <span className="truncate">{c.title}</span>
        )}
      </button>
      {editingConvId !== c.id && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setEditingConvId(c.id);
            }}
            className={`p-1 rounded text-gray-400 hover:text-gray-700 opacity-0 group-hover/conversation:opacity-100 ${activeConversationId === c.id ? 'opacity-100' : ''}`}
            title="Editar título"
          >
            <Pencil size={13} />
          </button>
          <button
            type="button"
            onClick={(e) => handleDeleteConversation(e, c.id)}
            className={`p-1 rounded text-gray-400 hover:text-red-600 opacity-0 group-hover/conversation:opacity-100 ${activeConversationId === c.id ? 'opacity-100' : ''}`}
            title="Eliminar"
          >
            <Trash2 size={13} />
          </button>
        </>
      )}
    </div>
  );

  return (
    <div
      className="wa-asistente-page flex h-full min-h-0 w-full max-w-full flex-1 flex-col overflow-hidden"
      style={{ colorScheme: 'light', forcedColorAdjust: 'none' }}
    >
    <div
      className="wa-asistente-shell relative flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden lg:flex-row"
      style={{ colorScheme: 'light', forcedColorAdjust: 'none' }}
    >
      {showConvPanel && !isDesktop && (
        <button
          type="button"
          className="wa-asistente-conv-backdrop lg:hidden"
          aria-label="Cerrar panel de conversaciones"
          onClick={closeConvPanel}
        />
      )}

      {showConvPanel && (
        <aside
          className="wa-asistente-conv-panel fixed top-14 bottom-0 left-0 z-[40] flex w-[min(88vw,19rem)] min-h-0 flex-col sm:top-16 lg:static lg:inset-auto lg:top-auto lg:bottom-auto lg:z-auto lg:w-64 lg:min-h-0 lg:shrink-0 xl:w-80"
          aria-label="Historial de conversaciones"
        >
          <div className="flex shrink-0 flex-col gap-3 border-b border-gray-200/80 p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="wa-asistente-sidebar-label px-1">Conversaciones</p>
              <button
                type="button"
                onClick={closeConvPanel}
                className="wa-asistente-icon-btn wa-asistente-icon-btn--accent"
                title="Ocultar historial"
                aria-label="Ocultar historial de conversaciones"
              >
                <PanelLeftClose size={18} />
              </button>
            </div>
            <button type="button" onClick={handleNewChat} className="wa-asistente-new-chat-btn">
              <Plus size={18} aria-hidden />
              Nueva conversación
            </button>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} aria-hidden />
              <input
                type="search"
                value={sidebarSearch}
                onChange={(e) => setSidebarSearch(e.target.value)}
                placeholder="Buscar conversación…"
                className="wa-asistente-sidebar-search"
                aria-label="Buscar conversación"
              />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain touch-pan-y px-1 py-2 [-webkit-overflow-scrolling:touch]">
            {conversationsLoading && (
              <p className="px-3 py-2 text-xs text-gray-500">Cargando…</p>
            )}
            {!conversationsLoading && conversationGroups.length === 0 && (
              <p className="px-3 py-4 text-center text-xs text-gray-500">Sin conversaciones guardadas</p>
            )}
            {conversationGroups.map((group) => (
              <div key={group.label} className="mb-2">
                <p className="wa-asistente-conv-group-label">{group.label}</p>
                {group.items.map(renderConversationItem)}
              </div>
            ))}
          </div>
          <p className="shrink-0 border-t border-gray-200/80 px-3 py-2 text-xs text-gray-500">
            {conversations.length} conversación{conversations.length === 1 ? '' : 'es'}
          </p>
        </aside>
      )}

      <div className="wa-asistente-main flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="wa-asistente-chat-header flex flex-shrink-0 flex-col gap-2 px-3 py-3 sm:px-4 lg:flex-row lg:items-start lg:justify-between lg:gap-3 lg:px-5">
          <div className="flex min-w-0 flex-1 items-start gap-2">
            {!convPanelOpen && (
              <button
                type="button"
                onClick={openConvPanel}
                className="wa-asistente-icon-btn wa-asistente-icon-btn--accent shrink-0"
                title="Mostrar historial"
                aria-label="Mostrar historial de conversaciones"
              >
                <PanelLeft size={18} />
              </button>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="wa-asistente-chat-title max-lg:whitespace-normal max-lg:break-words lg:truncate">{activeChatTitle}</h1>
              <p className="wa-asistente-chat-meta">{chatMetaLine}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleDownloadChat}
            disabled={messages.length <= 1}
            className="wa-asistente-download-btn w-full justify-center lg:w-auto lg:justify-start"
            title="Descargar conversación (HTML)"
          >
            <Download size={16} className="shrink-0" aria-hidden />
            <span className="lg:hidden">Descargar</span>
            <span className="hidden lg:inline">Descargar conversación</span>
          </button>
        </header>

        <div className="relative flex min-h-0 flex-1 flex-col">
          <div
            ref={chatMainRef}
            onScroll={handleChatScroll}
            className="flex min-h-0 flex-1 touch-pan-y flex-col overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch]"
          >
            <div className="mt-auto flex flex-col gap-3 px-3 py-4 sm:gap-4 sm:px-5 sm:py-5 lg:px-6 lg:py-6 max-w-3xl mx-auto w-full">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`wa-asistente-msg-row ${msg.role === 'user' ? 'is-user' : ''}`}
                >
                  <div
                    className={`wa-asistente-avatar ${msg.role === 'user' ? 'wa-asistente-avatar--user' : 'wa-asistente-avatar--ia'}`}
                    aria-hidden
                  >
                    {msg.role === 'user' ? 'Tú' : 'IA'}
                  </div>
                  <div
                    className={`wa-asistente-bubble min-w-0 flex-1 ${
                      msg.role === 'user' ? 'wa-asistente-bubble--user' : 'wa-asistente-bubble--assistant'
                    }`}
                  >
                    {msg.role === 'user' ? (
                      <div
                        className="break-words"
                        dangerouslySetInnerHTML={{ __html: formatMessageHtml(msg.content, { isUser: true }) }}
                      />
                    ) : (
                      renderMessageContent(msg.content, idx)
                    )}
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="wa-asistente-msg-row">
                  <div className="wa-asistente-avatar wa-asistente-avatar--ia" aria-hidden>IA</div>
                  <div className="wa-asistente-bubble wa-asistente-bubble--assistant flex gap-1 py-4">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-bounce" />
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-bounce [animation-delay:150ms]" />
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-bounce [animation-delay:300ms]" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {showScrollFab && (
            <button
              type="button"
              onClick={handleScrollToBottom}
              className="wa-asistente-scroll-fab"
              title="Ir al final"
              aria-label="Ir al final de la conversación"
            >
              <ChevronDown size={18} />
            </button>
          )}
        </div>

        <div className="wa-asistente-input-area shrink-0 px-3 pt-2 pb-3 sm:px-4 sm:pb-4 lg:px-5">
          <form onSubmit={handleSubmit} className="wa-asistente-input-stack mx-auto max-w-3xl">
            {!isLoading && (
              <AssistantSuggestPill onClick={handleSuggestPillClick} disabled={isLoading} />
            )}
            <div className="wa-asistente-input-shell w-full">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                placeholder="Escribe un mensaje…"
                rows={1}
                className="wa-asistente-textarea max-h-32 min-h-[2.75rem]"
              />
              <button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="wa-asistente-send-btn"
                title="Enviar"
              >
                <Send size={18} />
              </button>
            </div>
            <p className="mt-2 flex items-center justify-center gap-1 text-center text-[11px] text-gray-400">
              <Sparkles size={12} className="shrink-0" aria-hidden />
              <span>La IA puede cometer errores. Verifica lo importante.</span>
            </p>
          </form>
        </div>
      </div>
    </div>
    </div>
  );
}